import { Editor, Node, mergeAttributes, type JSONContent } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import type { ContentBlock } from './api';

type RichEditorOptions = {
  root: HTMLElement;
  onChange?: (blocks: ContentBlock[]) => void;
  uploadImage?: (file: File) => Promise<string>;
  getTagOptions?: () => Array<{ id: number; name: string; slug: string }>;
};

type RichEditorHandle = {
  getBlocks: () => ContentBlock[] | null;
  setBlocks: (blocks?: ContentBlock[] | null) => void;
  destroy: () => void;
};

type ModalField = {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required?: boolean;
  options?: string[];
  placeholder?: string;
};

type ParagraphLink = { title: string; url: string };
type InlineSpan = { text: string; marks: string[]; href?: string };

const uid = (): string => `b-${Math.random().toString(36).slice(2, 10)}`;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const escapeAttribute = (value: string): string => value.replace(/"/g, '&quot;');

const parseLines = (raw: string): string[] =>
  raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const getText = (value: unknown): string => (typeof value === 'string' ? value : '');

const safeJsonParse = <T>(value: string, fallback: T): T => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const parseBlockPayload = (value: unknown): ContentBlock | null => {
  if (!value) return null;
  if (typeof value === 'object') {
    const payload = value as ContentBlock;
    return payload.type ? payload : null;
  }
  if (typeof value === 'string') {
    const decoded = value.includes('%') ? decodeURIComponent(value) : value;
    const parsed = safeJsonParse<ContentBlock | null>(decoded, null);
    return parsed?.type ? parsed : null;
  }
  return null;
};


const dedupeLinks = (links: ParagraphLink[]): ParagraphLink[] => {
  const unique = new Map<string, ParagraphLink>();
  for (const link of links) unique.set(`${link.title}::${link.url}`, link);
  return Array.from(unique.values());
};

const createInlineStyles = (): void => {
  if (document.getElementById('mj-rich-editor-inline')) return;
  const style = document.createElement('style');
  style.id = 'mj-rich-editor-inline';
  style.textContent = `
    .mj-rich-shell{border:1px solid #d1d5db;border-radius:14px;background:#f8fafc;overflow:visible}
    .mj-rich-toolbar{padding:10px;border-bottom:1px solid #e5e7eb;background:#f3f4f6;display:flex;flex-direction:column;gap:8px}
    .mj-rich-toolbar-main{display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding:8px;border-radius:16px;background:#e5e7eb}
    .mj-rich-sep{width:1px;height:24px;background:#cbd5e1;margin:0 4px}
    .mj-editor-body{padding:0 14px 14px}
    .mj-editor-surface{min-height:360px;max-height:700px;overflow:auto;padding:24px;font-size:16px;line-height:1.7;color:#0f172a;max-width:860px;margin:18px auto;background:#fff;border:1px solid #e2e8f0;border-radius:8px;box-shadow:0 1px 3px rgba(2,6,23,.06)}
    .mj-editor-surface:focus{outline:none;border-color:#93c5fd;box-shadow:0 0 0 3px rgba(59,130,246,.12)}
    .mj-editor-surface h1{font-size:2rem;font-weight:800;margin:10px 0}
    .mj-editor-surface h2{font-size:1.625rem;font-weight:700;margin:10px 0}
    .mj-editor-surface h3{font-size:1.3rem;font-weight:700;margin:10px 0}
    .mj-editor-surface h4{font-size:1.125rem;font-weight:700;margin:10px 0}
    .mj-editor-surface blockquote{border-left:4px solid #93c5fd;background:#eff6ff;padding:8px 12px;border-radius:0 10px 10px 0;margin:10px 0}
    .mj-editor-surface pre{background:#111827;color:#e5e7eb;border-radius:10px;padding:12px;white-space:pre-wrap}
    .mj-editor-surface a{color:#2563eb;text-decoration:underline}
    .mj-editor-surface img{max-width:100%;height:auto}
    .mj-q-card{position:relative;border:0;background:transparent;border-radius:0;padding:0;margin:10px 0}
    .mj-q-card-actions{display:flex;gap:6px;justify-content:flex-end;margin:0 0 8px}
    .mj-q-preview{margin-top:0;background:transparent;border:0;border-radius:0;padding:0}
    .mj-q-image{width:100%;max-height:420px;display:block;object-fit:cover;border-radius:10px;background:#f8fafc}
    .mj-q-callout{border-left:4px solid #93c5fd;background:#eff6ff;padding:10px 12px;border-radius:6px}
    .mj-q-callout--warning{border-left-color:#f59e0b;background:#fffbeb}
    .mj-q-callout--success{border-left-color:#16a34a;background:#f0fdf4}
    .mj-q-callout--danger{border-left-color:#dc2626;background:#fef2f2}
    .mj-q-callout-title{font-weight:700;margin-bottom:4px}
    .mj-q-gallery{display:grid;gap:8px;align-items:start}
    .mj-q-gallery--grid{grid-template-columns:repeat(auto-fill,minmax(120px,1fr))}
    .mj-q-gallery--carousel{grid-auto-flow:column;grid-auto-columns:minmax(160px,1fr);overflow-x:auto;padding-bottom:4px}
    .mj-q-thumb{border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;background:#f8fafc;margin:0}
    .mj-q-thumb img{width:100%;height:100px;object-fit:cover;display:block}
    .mj-q-thumb figcaption{font-size:11px;color:#64748b;padding:6px}
    .mj-q-embed{border:1px solid #cbd5e1;border-radius:6px;overflow:hidden;background:#fff}
    .mj-q-embed iframe{display:block;width:100%;aspect-ratio:16/9;border:0}
    .mj-q-table-wrap{overflow:auto}
    .mj-q-table{width:100%;border-collapse:collapse;font-size:13px}
    .mj-q-table th,.mj-q-table td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left;vertical-align:top}
    .mj-q-table th{background:#f8fafc}
    .mj-q-links{display:grid;gap:6px}
    .mj-q-link-item{padding:8px;border:1px solid #e2e8f0;border-radius:6px;background:#fff}
    .mj-q-cta{border:1px solid #bfdbfe;border-radius:10px;padding:12px;background:#eff6ff}
    .mj-q-cta-btn{display:inline-flex;margin-top:8px;padding:6px 10px;border-radius:8px;background:#2563eb;color:#fff;text-decoration:none;font-size:12px;font-weight:600}
    .mj-q-cta-btn--secondary{background:#475569}
    .mj-q-divider{height:1px;background:#cbd5e1;border:0;margin:10px 0}
    .mj-q-tags{display:flex;flex-wrap:wrap;gap:6px}
    .mj-q-tag{font-size:11px;padding:4px 8px;border-radius:999px;background:#eef2ff;color:#3730a3;border:1px solid #c7d2fe}
    .mj-modal-backdrop{position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center}
    .mj-modal{width:min(760px,92vw);max-height:88vh;overflow:auto;border:1px solid #cbd5e1;border-radius:14px;background:#fff;box-shadow:0 20px 70px rgba(2,6,23,.25)}
    .mj-modal-head{padding:14px 16px;border-bottom:1px solid #e2e8f0;font-weight:700}
    .mj-modal-body{padding:14px 16px;display:grid;gap:10px}
    .mj-modal-label{display:flex;flex-direction:column;gap:4px;font-size:12px;color:#334155}
    .mj-modal-input,.mj-modal-select,.mj-modal-textarea{border:1px solid #cbd5e1;border-radius:8px;padding:8px 10px;font-size:13px}
    .mj-modal-textarea{min-height:96px;resize:vertical}
    .mj-modal-foot{padding:12px 16px;border-top:1px solid #e2e8f0;display:flex;justify-content:flex-end;gap:8px}
    .mj-btn{border:1px solid #cbd5e1;background:#fff;border-radius:8px;padding:6px 10px;font-size:12px;cursor:pointer}
    .mj-btn:hover{background:#f8fafc}
    .mj-btn-primary{border-color:#2563eb;background:#2563eb;color:#fff}
    .mj-btn-primary:hover{background:#1d4ed8}
    .mj-btn-danger{border-color:#dc2626;background:#dc2626;color:#fff}
    .mj-btn-danger:hover{background:#b91c1c}
    .mj-icon-btn{display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border:1px solid #cbd5e1;background:#fff;border-radius:9px;cursor:pointer;color:#334155}
    .mj-icon-btn:hover{background:#f8fafc}
    .mj-icon-btn .material-symbols-outlined{font-size:18px;line-height:1}
    .mj-icon-btn[data-wide="1"]{width:auto;padding:0 10px;gap:4px}
    .mj-icon-label{font-size:12px;font-weight:600}
    .mj-toolbar-select{height:34px;border:1px solid #cbd5e1;background:#fff;border-radius:9px;padding:0 10px;font-size:12px;color:#334155}
    .mj-rich-toolbar{user-select:none}
    .mj-editor-content{outline:none}
    .mj-editor-surface:focus-within{outline:none;border-color:#93c5fd;box-shadow:0 0 0 3px rgba(59,130,246,.12)}
    .mj-editor-surface ul{list-style:disc outside;padding-left:1.5em;margin:0.5em 0}
    .mj-editor-surface ol{list-style:decimal outside;padding-left:1.5em;margin:0.5em 0}
    .mj-editor-surface li{margin:0.2em 0}
    .mj-editor-surface li>p{margin:0}
    .mj-editor-surface ul ul{list-style:circle;margin:0}
    .mj-editor-surface ul ul ul{list-style:square;margin:0}
    .mj-upload-zone{border:2px dashed #cbd5e1;border-radius:10px;padding:28px 16px;text-align:center;cursor:pointer;color:#64748b;font-size:13px;transition:border-color .15s,background .15s;background:#f8fafc}
    .mj-upload-zone:hover,.mj-upload-zone.drag-over{border-color:#3b82f6;background:#eff6ff;color:#2563eb}
    .mj-upload-zone-icon{font-size:32px;margin-bottom:6px;display:block;color:#94a3b8}
    .mj-img-preview{position:relative;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;background:#f1f5f9}
    .mj-img-preview img{width:100%;max-height:200px;object-fit:contain;display:block}
    .mj-img-preview-remove{position:absolute;top:6px;right:6px;width:24px;height:24px;border-radius:50%;background:rgba(15,23,42,.55);color:#fff;border:0;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1}
    .mj-gallery-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px}
    .mj-gallery-item{position:relative;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;background:#f8fafc}
    .mj-gallery-item img{width:100%;height:100px;object-fit:cover;display:block}
    .mj-gallery-item-foot{padding:6px}
    .mj-gallery-item-remove{position:absolute;top:4px;right:4px;width:20px;height:20px;border-radius:50%;background:rgba(15,23,42,.55);color:#fff;border:0;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;line-height:1}
    .mj-gallery-uploading{display:flex;align-items:center;justify-content:center;height:100px;font-size:12px;color:#64748b}
    .mj-table-editor{overflow:auto}
    .mj-table-editor table{border-collapse:collapse;width:100%}
    .mj-table-editor th,.mj-table-editor td{border:1px solid #cbd5e1;padding:0}
    .mj-table-editor th input,.mj-table-editor td input{border:0;outline:0;padding:6px 8px;font-size:13px;width:100%;background:transparent;box-sizing:border-box}
    .mj-table-editor th{background:#f1f5f9}
    .mj-table-editor th input{font-weight:600}
    .mj-table-controls{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap}
    .mj-links-list{display:grid;gap:6px}
    .mj-link-row{display:grid;grid-template-columns:1fr 1fr auto;gap:6px;align-items:center;border:1px solid #e2e8f0;border-radius:8px;padding:8px}
    .mj-link-row input{border:1px solid #cbd5e1;border-radius:6px;padding:6px 8px;font-size:13px;min-width:0}
    .mj-link-row-source{grid-column:1/-1}
    .mj-uploading-spinner{display:inline-block;width:16px;height:16px;border:2px solid #cbd5e1;border-top-color:#3b82f6;border-radius:50%;animation:mj-spin .7s linear infinite;vertical-align:middle}
    .mj-upload-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
    .mj-modal-help{font-size:12px;color:#64748b}
    .mj-hidden{display:none !important}
    @keyframes mj-spin{to{transform:rotate(360deg)}}
  `;
  document.head.appendChild(style);
};

const youtubeEmbedUrl = (url: string): string => {
  const watch = url.match(/[?&]v=([^&]+)/i);
  if (watch?.[1]) return `https://www.youtube.com/embed/${watch[1]}`;
  const short = url.match(/youtu\.be\/([^?&]+)/i);
  if (short?.[1]) return `https://www.youtube.com/embed/${short[1]}`;
  return url;
};

const vimeoEmbedUrl = (url: string): string => {
  const match = url.match(/vimeo\.com\/(\d+)/i);
  if (match?.[1]) return `https://player.vimeo.com/video/${match[1]}`;
  return url;
};

const renderBlockPreviewHtml = (block: ContentBlock): string => {
  const type = String(block.type || '');
  if (type === 'list') {
    const style = getText(block.style) || 'bullet';
    const items = Array.isArray(block.items) ? block.items : [];
    if (style === 'ordered') return `<ol>${items.map((item) => `<li>${escapeHtml(String(item))}</li>`).join('')}</ol>`;
    if (style === 'check') return `<ul>${items.map((item) => `<li>☑ ${escapeHtml(String(item))}</li>`).join('')}</ul>`;
    return `<ul>${items.map((item) => `<li>${escapeHtml(String(item))}</li>`).join('')}</ul>`;
  }

  if (type === 'callout') {
    const variant = getText(block.variant) || 'info';
    return `<div class="mj-q-callout mj-q-callout--${escapeAttribute(escapeHtml(variant))}">
      ${getText(block.title) ? `<div class="mj-q-callout-title">${escapeHtml(getText(block.title))}</div>` : ''}
      <div>${escapeHtml(getText(block.text))}</div>
      ${getText(block.icon) ? `<div style="margin-top:6px;font-size:12px;color:#64748b">icon: ${escapeHtml(getText(block.icon))}</div>` : ''}
    </div>`;
  }

  if (type === 'image') {
    const caption = getText(block.caption);
    const credit = getText(block.credit);
    const ratio = getText(block.ratio);
    const ratioCss = ratio.includes(':') ? ratio.replace(':', ' / ') : '';
    const ratioStyle = ratioCss ? ` style="aspect-ratio:${escapeAttribute(ratioCss)}"` : '';
    return `<figure>
      <img class="mj-q-image" src="${escapeAttribute(escapeHtml(getText(block.url)))}" alt="${escapeAttribute(escapeHtml(getText(block.alt)))}"${ratioStyle} />
      ${(caption || credit || ratio) ? `<figcaption>${[caption, credit ? `© ${credit}` : '', ratio ? `ratio ${ratio}` : ''].filter(Boolean).map(escapeHtml).join(' · ')}</figcaption>` : ''}
    </figure>`;
  }

  if (type === 'gallery') {
    const layout = getText(block.layout) || 'grid';
    const items = Array.isArray(block.items) ? block.items : [];
    return `<div class="mj-q-gallery mj-q-gallery--${escapeAttribute(escapeHtml(layout))}">
      ${items
        .map((itemValue) => {
          const item = itemValue as Record<string, unknown>;
          return `<figure class="mj-q-thumb">
            <img src="${escapeAttribute(escapeHtml(getText(item.url)))}" alt="${escapeAttribute(escapeHtml(getText(item.alt)))}"/>
            ${getText(item.caption) ? `<figcaption>${escapeHtml(getText(item.caption))}</figcaption>` : ''}
          </figure>`;
        })
        .join('')}
    </div>`;
  }

  if (type === 'embed') {
    const provider = getText(block.provider);
    const srcRaw = getText(block.url);
    const src = provider === 'youtube' ? youtubeEmbedUrl(srcRaw) : provider === 'vimeo' ? vimeoEmbedUrl(srcRaw) : '';
    if (src) {
      return `<div class="mj-q-embed"><iframe src="${escapeAttribute(escapeHtml(src))}" allowfullscreen loading="lazy"></iframe></div>`;
    }
    return `<div><a href="${escapeAttribute(escapeHtml(srcRaw))}" target="_blank" rel="noopener noreferrer">${escapeHtml(getText(block.title) || srcRaw || 'Open embed')}</a></div>`;
  }

  if (type === 'table') {
    const columns = Array.isArray(block.columns) ? block.columns : [];
    const rows = Array.isArray(block.rows) ? block.rows : [];
    return `<div class="mj-q-table-wrap">
      <table class="mj-q-table">
        <thead><tr>${columns.map((column) => `<th>${escapeHtml(String(column))}</th>`).join('')}</tr></thead>
        <tbody>${rows
          .map((rowValue) => {
            const row = Array.isArray(rowValue) ? rowValue : [];
            return `<tr>${row.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join('')}</tr>`;
          })
          .join('')}</tbody>
      </table>
      ${getText(block.caption) ? `<div style="margin-top:6px;font-size:12px;color:#64748b">${escapeHtml(getText(block.caption))}</div>` : ''}
    </div>`;
  }

  if (type === 'related_links') {
    const items = Array.isArray(block.items) ? block.items : [];
    return `<div class="mj-q-links">
      ${getText(block.title) ? `<strong>${escapeHtml(getText(block.title))}</strong>` : ''}
      ${items
        .map((itemValue) => {
          const item = itemValue as Record<string, unknown>;
          return `<div class="mj-q-link-item">
            <a href="${escapeAttribute(escapeHtml(getText(item.url)))}" target="_blank" rel="noopener noreferrer">${escapeHtml(getText(item.title) || getText(item.url))}</a>
            ${getText(item.source) ? `<div style="font-size:11px;color:#64748b;margin-top:2px">${escapeHtml(getText(item.source))}</div>` : ''}
          </div>`;
        })
        .join('')}
    </div>`;
  }

  if (type === 'cta') {
    const variant = getText(block.variant) || 'primary';
    return `<div class="mj-q-cta">
      <div style="font-weight:700">${escapeHtml(getText(block.title))}</div>
      <div style="margin-top:4px">${escapeHtml(getText(block.text))}</div>
      <a class="mj-q-cta-btn ${variant === 'secondary' ? 'mj-q-cta-btn--secondary' : ''}" href="${escapeAttribute(
        escapeHtml(getText(block.button_url))
      )}" target="_blank" rel="noopener noreferrer">${escapeHtml(getText(block.button_text) || 'Open')}</a>
    </div>`;
  }

  if (type === 'code') {
    const language = getText(block.language);
    const caption = getText(block.caption);
    return `<div>
      ${language ? `<div style="font-size:11px;color:#94a3b8;margin-bottom:4px">${escapeHtml(language)}</div>` : ''}
      <pre>${escapeHtml(getText(block.content))}</pre>
      ${caption ? `<div style="font-size:12px;color:#64748b;margin-top:4px">${escapeHtml(caption)}</div>` : ''}
    </div>`;
  }

  if (type === 'divider') {
    return getText(block.style) === 'space' ? `<div style="height:20px"></div>` : `<hr class="mj-q-divider"/>`;
  }

  if (type === 'tags') {
    const items = Array.isArray(block.items) ? block.items : [];
    return `<div class="mj-q-tags">${items
      .map((itemValue) => {
        const item = itemValue as Record<string, unknown>;
        return `<span class="mj-q-tag">${escapeHtml(`#${getText(item.slug) || getText(item.name) || String(item.id || '')}`)}</span>`;
      })
      .join('')}</div>`;
  }

  return `<pre>${escapeHtml(JSON.stringify(block, null, 2))}</pre>`;
};

const cardInnerHtml = (block: ContentBlock): string => `
  <div class="mj-q-card-actions">
    <button type="button" class="mj-btn" data-card-edit="${escapeAttribute(String(block.id || ''))}" title="Edit block">Edit</button>
    <button type="button" class="mj-btn mj-btn-danger" data-card-remove="${escapeAttribute(String(block.id || ''))}" title="Delete block">Delete</button>
  </div>
  <div class="mj-q-preview">${renderBlockPreviewHtml(block)}</div>
`;

const modalForm = async (
  title: string,
  fields: ModalField[],
  initial: Record<string, unknown>
): Promise<Record<string, string> | null> =>
  new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'mj-modal-backdrop';

    const body = fields
      .map((field) => {
        const value = String(initial[field.key] ?? '');
        if (field.type === 'textarea') {
          return `<label class="mj-modal-label">${escapeHtml(field.label)}${field.required ? ' *' : ''}
            <textarea class="mj-modal-textarea" data-key="${escapeHtml(field.key)}" placeholder="${escapeHtml(
            field.placeholder || ''
          )}">${escapeHtml(value)}</textarea>
          </label>`;
        }
        if (field.type === 'select') {
          return `<label class="mj-modal-label">${escapeHtml(field.label)}${field.required ? ' *' : ''}
            <select class="mj-modal-select" data-key="${escapeHtml(field.key)}">
              ${(field.options || [])
                .map((option) => `<option value="${escapeHtml(option)}" ${option === value ? 'selected' : ''}>${escapeHtml(option)}</option>`)
                .join('')}
            </select>
          </label>`;
        }
        return `<label class="mj-modal-label">${escapeHtml(field.label)}${field.required ? ' *' : ''}
          <input class="mj-modal-input" data-key="${escapeHtml(field.key)}" value="${escapeHtml(value)}" placeholder="${escapeHtml(
          field.placeholder || ''
        )}" />
        </label>`;
      })
      .join('');

    backdrop.innerHTML = `
      <div class="mj-modal" role="dialog" aria-modal="true">
        <div class="mj-modal-head">${escapeHtml(title)}</div>
        <div class="mj-modal-body">${body}</div>
        <div class="mj-modal-foot">
          <button type="button" data-action="cancel" class="mj-btn">Cancel</button>
          <button type="button" data-action="save" class="mj-btn mj-btn-primary">Save</button>
        </div>
      </div>
    `;

    const close = (payload: Record<string, string> | null) => {
      backdrop.remove();
      resolve(payload);
    };

    backdrop.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target === backdrop || target.closest('[data-action="cancel"]')) {
        close(null);
        return;
      }
      if (!target.closest('[data-action="save"]')) return;

      const values: Record<string, string> = {};
      for (const field of fields) {
        const control = backdrop.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-key="${field.key}"]`);
        const value = control?.value.trim() || '';
        if (field.required && !value) {
          control?.focus();
          return;
        }
        values[field.key] = value;
      }
      close(values);
    });

    document.body.appendChild(backdrop);
    backdrop.querySelector<HTMLElement>('input,textarea,select')?.focus();
  });

// ── Image modal with file upload ─────────────────────────────────────────────

type ImageModalResult = {
  url: string;
  alt: string;
  caption: string;
  ratio: string;
  credit: string;
} | null;

const openImageModal = (
  uploadFn: ((file: File) => Promise<string>) | undefined,
  initial?: Record<string, unknown>
): Promise<ImageModalResult> =>
  new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'mj-modal-backdrop';

    const initUrl = getText(initial?.url);
    const initAlt = getText(initial?.alt);
    const initCaption = getText(initial?.caption);
    const initRatio = getText(initial?.ratio) || '16:9';
    const initCredit = getText(initial?.credit);

    backdrop.innerHTML = `
      <div class="mj-modal" role="dialog" aria-modal="true" style="width:min(560px,92vw)">
        <div class="mj-modal-head">Image</div>
        <div class="mj-modal-body">
          ${uploadFn ? `
            <div class="mj-upload-actions">
              <button type="button" class="mj-btn" id="mj-img-pick-btn">Выбрать файл</button>
              <span class="mj-modal-help" id="mj-img-file-name">Файл не выбран</span>
              <input type="file" accept="image/*" class="mj-hidden" id="mj-img-file-input" />
            </div>
            <div class="mj-upload-zone" id="mj-img-drop">
              <span class="mj-upload-zone-icon material-symbols-outlined">upload_file</span>
              <div>Или перетащите файл сюда</div>
              <div style="margin-top:4px;font-size:11px;color:#94a3b8">JPG, PNG, WebP — до 10 МБ</div>
            </div>
            <div id="mj-img-preview-wrap" style="${initUrl ? '' : 'display:none'}">
              <div class="mj-img-preview">
                <img id="mj-img-preview-img" src="${escapeAttribute(escapeHtml(initUrl))}" alt="" />
                <button type="button" class="mj-img-preview-remove" id="mj-img-remove-btn" title="Remove">✕</button>
              </div>
            </div>
            <div id="mj-img-uploading" style="display:none;font-size:13px;color:#64748b">
              <span class="mj-uploading-spinner"></span> Загружаем...
            </div>
          ` : ''}
          <label class="mj-modal-label">URL изображения (https) *
            <input class="mj-modal-input" id="mj-img-url" value="${escapeAttribute(escapeHtml(initUrl))}" placeholder="https://..." ${uploadFn ? 'readonly' : ''} />
          </label>
          ${uploadFn ? `<div class="mj-modal-help">URL заполнится автоматически после загрузки файла.</div>` : ''}
          <label class="mj-modal-label">Alt
            <input class="mj-modal-input" id="mj-img-alt" value="${escapeAttribute(escapeHtml(initAlt))}" placeholder="Описание для screen reader" />
          </label>
          <label class="mj-modal-label">Подпись
            <input class="mj-modal-input" id="mj-img-caption" value="${escapeAttribute(escapeHtml(initCaption))}" placeholder="Подпись под изображением" />
          </label>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
            <label class="mj-modal-label">Соотношение сторон
              <select class="mj-modal-select" id="mj-img-ratio">
                ${['16:9','4:3','1:1','3:2'].map((r) => `<option value="${r}" ${r === initRatio ? 'selected' : ''}>${r}</option>`).join('')}
              </select>
            </label>
            <label class="mj-modal-label">Фото © (credit)
              <input class="mj-modal-input" id="mj-img-credit" value="${escapeAttribute(escapeHtml(initCredit))}" placeholder="Автор" />
            </label>
          </div>
        </div>
        <div class="mj-modal-foot">
          <button type="button" data-action="cancel" class="mj-btn">Отмена</button>
          <button type="button" data-action="save" class="mj-btn mj-btn-primary">Сохранить</button>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    const urlInput = backdrop.querySelector<HTMLInputElement>('#mj-img-url')!;
    const previewWrap = backdrop.querySelector<HTMLElement>('#mj-img-preview-wrap');
    const previewImg = backdrop.querySelector<HTMLImageElement>('#mj-img-preview-img');
    const uploadingEl = backdrop.querySelector<HTMLElement>('#mj-img-uploading');
    const pickBtn = backdrop.querySelector<HTMLButtonElement>('#mj-img-pick-btn');
    const fileNameEl = backdrop.querySelector<HTMLElement>('#mj-img-file-name');
    const dropZone = backdrop.querySelector<HTMLElement>('#mj-img-drop');
    const fileInput = backdrop.querySelector<HTMLInputElement>('#mj-img-file-input');
    const removeBtn = backdrop.querySelector<HTMLButtonElement>('#mj-img-remove-btn');
    const saveBtn = backdrop.querySelector<HTMLButtonElement>('[data-action="save"]');

    const showPreview = (url: string) => {
      urlInput.value = url;
      if (previewImg) previewImg.src = url;
      if (previewWrap) previewWrap.style.display = '';
    };

    const hidePreview = () => {
      urlInput.value = '';
      if (previewWrap) previewWrap.style.display = 'none';
    };

    const doUpload = async (file: File) => {
      if (!uploadFn) return;
      if (fileNameEl) fileNameEl.textContent = file.name;
      if (uploadingEl) uploadingEl.style.display = '';
      if (dropZone) dropZone.style.display = 'none';
      if (saveBtn) saveBtn.disabled = true;
      try {
        const url = await uploadFn(file);
        showPreview(url);
      } catch (err) {
        window.alert(`Ошибка загрузки: ${String(err)}`);
        if (dropZone) dropZone.style.display = '';
      } finally {
        if (uploadingEl) uploadingEl.style.display = 'none';
        if (saveBtn) saveBtn.disabled = false;
      }
    };

    if (dropZone && fileInput) {
      pickBtn?.addEventListener('click', () => fileInput.click());
      dropZone.addEventListener('click', () => fileInput.click());
      dropZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropZone.classList.add('drag-over');
      });
      dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
      dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropZone.classList.remove('drag-over');
        const file = e.dataTransfer?.files[0];
        if (file) void doUpload(file);
      });
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file) void doUpload(file);
        fileInput.value = '';
      });
    }

    removeBtn?.addEventListener('click', () => {
      hidePreview();
      if (dropZone) dropZone.style.display = '';
    });

    const close = (result: ImageModalResult) => {
      backdrop.remove();
      resolve(result);
    };

    backdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === backdrop || target.closest('[data-action="cancel"]')) {
        close(null);
        return;
      }
      if (!target.closest('[data-action="save"]')) return;

      const url = urlInput.value.trim();
      if (!url) { urlInput.focus(); return; }

      close({
        url,
        alt: (backdrop.querySelector<HTMLInputElement>('#mj-img-alt')?.value || '').trim(),
        caption: (backdrop.querySelector<HTMLInputElement>('#mj-img-caption')?.value || '').trim(),
        ratio: backdrop.querySelector<HTMLSelectElement>('#mj-img-ratio')?.value || '16:9',
        credit: (backdrop.querySelector<HTMLInputElement>('#mj-img-credit')?.value || '').trim(),
      });
    });

    urlInput.focus();
  });

// ── Gallery modal with multi-file upload ────────────────────────────────────

type GalleryItem = { url: string; alt: string; caption: string; credit: string };
type GalleryModalResult = { layout: string; items: GalleryItem[] } | null;
type TagsModalResult = { items: Array<{ id: number; name: string; slug: string }> } | null;

const openGalleryModal = (
  uploadFn: ((file: File) => Promise<string>) | undefined,
  initial?: Record<string, unknown>
): Promise<GalleryModalResult> =>
  new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'mj-modal-backdrop';

    const initLayout = getText(initial?.layout) || 'grid';
    const initItems: GalleryItem[] = (Array.isArray(initial?.items) ? initial!.items : []).map((iv) => {
      const i = iv as Record<string, unknown>;
      return { url: getText(i.url), alt: getText(i.alt), caption: getText(i.caption), credit: getText(i.credit) };
    });

    backdrop.innerHTML = `
      <div class="mj-modal" role="dialog" aria-modal="true" style="width:min(700px,92vw)">
        <div class="mj-modal-head">Gallery</div>
        <div class="mj-modal-body">
          <label class="mj-modal-label">Layout
            <select class="mj-modal-select" id="mj-gal-layout">
              <option value="grid" ${initLayout === 'grid' ? 'selected' : ''}>Grid</option>
              <option value="carousel" ${initLayout === 'carousel' ? 'selected' : ''}>Carousel</option>
            </select>
          </label>
          <div class="mj-upload-actions">
            ${uploadFn ? `<button type="button" class="mj-btn" id="mj-gal-pick-btn">Выбрать файлы</button>` : ''}
            <button type="button" class="mj-btn" id="mj-gal-add-url-btn">Добавить URL</button>
            ${uploadFn ? `<input type="file" accept="image/*" multiple class="mj-hidden" id="mj-gal-file-input" />` : ''}
          </div>
          ${uploadFn ? `
            <div class="mj-upload-zone" id="mj-gal-drop">
              <span class="mj-upload-zone-icon material-symbols-outlined">photo_library</span>
              <div>Или перетащите файлы сюда</div>
              <div style="margin-top:4px;font-size:11px;color:#94a3b8">Можно выбрать несколько файлов</div>
            </div>
          ` : ''}
          <div id="mj-gal-grid" class="mj-gallery-grid"></div>
          <div id="mj-gal-status" style="font-size:12px;color:#64748b;display:none"></div>
        </div>
        <div class="mj-modal-foot">
          <button type="button" data-action="cancel" class="mj-btn">Отмена</button>
          <button type="button" data-action="save" class="mj-btn mj-btn-primary">Сохранить</button>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    const grid = backdrop.querySelector<HTMLElement>('#mj-gal-grid')!;
    const statusEl = backdrop.querySelector<HTMLElement>('#mj-gal-status')!;
    const pickBtn = backdrop.querySelector<HTMLButtonElement>('#mj-gal-pick-btn');
    const addUrlBtn = backdrop.querySelector<HTMLButtonElement>('#mj-gal-add-url-btn');
    const dropZone = backdrop.querySelector<HTMLElement>('#mj-gal-drop');
    const fileInput = backdrop.querySelector<HTMLInputElement>('#mj-gal-file-input');

    // items state
    const items: GalleryItem[] = [...initItems];

    const renderGrid = () => {
      grid.innerHTML = items
        .map(
          (item, idx) => `
          <div class="mj-gallery-item" data-gal-idx="${idx}">
            <img src="${escapeAttribute(escapeHtml(item.url))}" alt="${escapeAttribute(escapeHtml(item.alt))}" />
            <button type="button" class="mj-gallery-item-remove" data-remove="${idx}" title="Удалить">✕</button>
            <div class="mj-gallery-item-foot">
              <input class="mj-modal-input" style="width:100%;box-sizing:border-box;margin-bottom:4px;font-size:11px" data-gal-field="url" data-gal-idx="${idx}" value="${escapeAttribute(escapeHtml(item.url))}" placeholder="https://..." />
              <input class="mj-modal-input" style="width:100%;box-sizing:border-box;margin-bottom:4px;font-size:11px" data-gal-field="alt" data-gal-idx="${idx}" value="${escapeAttribute(escapeHtml(item.alt))}" placeholder="Alt" />
              <input class="mj-modal-input" style="width:100%;box-sizing:border-box;margin-bottom:4px;font-size:11px" data-gal-field="caption" data-gal-idx="${idx}" value="${escapeAttribute(escapeHtml(item.caption))}" placeholder="Подпись" />
              <input class="mj-modal-input" style="width:100%;box-sizing:border-box;font-size:11px" data-gal-field="credit" data-gal-idx="${idx}" value="${escapeAttribute(escapeHtml(item.credit))}" placeholder="Credit" />
            </div>
          </div>
        `
        )
        .join('');

      // attach input listeners
      grid.querySelectorAll<HTMLInputElement>('[data-gal-field]').forEach((input) => {
        input.addEventListener('input', () => {
          const idx = Number(input.dataset.galIdx);
          const field = input.dataset.galField as 'url' | 'alt' | 'caption' | 'credit';
          if (items[idx]) items[idx][field] = input.value;
        });
      });

      grid.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = Number(btn.dataset.remove);
          items.splice(idx, 1);
          renderGrid();
        });
      });
    };

    renderGrid();

    const addUploadingPlaceholder = (): HTMLElement => {
      const el = document.createElement('div');
      el.className = 'mj-gallery-item';
      el.innerHTML = `<div class="mj-gallery-uploading"><span class="mj-uploading-spinner"></span></div>`;
      grid.appendChild(el);
      return el;
    };

    const doUploadFiles = async (files: FileList | File[]) => {
      if (!uploadFn) return;
      const fileArr = Array.from(files);
      if (!fileArr.length) return;
      statusEl.textContent = `Загружаем ${fileArr.length} файл(ов)...`;
      statusEl.style.display = '';
      const placeholders = fileArr.map(() => addUploadingPlaceholder());
      let failed = 0;

      await Promise.all(
        fileArr.map(async (file, i) => {
          try {
            const url = await uploadFn(file);
            items.push({ url, alt: file.name, caption: '', credit: '' });
          } catch {
            failed += 1;
          } finally {
            placeholders[i]?.remove();
          }
        })
      );

      statusEl.style.display = 'none';
      renderGrid();
      if (failed > 0) {
        window.alert(`Не удалось загрузить ${failed} файл(ов).`);
      }
    };

    if (dropZone && fileInput) {
      pickBtn?.addEventListener('click', () => fileInput.click());
      dropZone.addEventListener('click', () => fileInput.click());
      dropZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropZone.classList.add('drag-over');
      });
      dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
      dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropZone.classList.remove('drag-over');
        if (e.dataTransfer?.files.length) void doUploadFiles(e.dataTransfer.files);
      });
      fileInput.addEventListener('change', () => {
        if (fileInput.files?.length) void doUploadFiles(fileInput.files);
        fileInput.value = '';
      });
    }

    addUrlBtn?.addEventListener('click', () => {
      const raw = window.prompt('Введите URL изображения (https://...)');
      const url = (raw || '').trim();
      if (!url) return;
      items.push({ url, alt: '', caption: '', credit: '' });
      renderGrid();
    });

    const close = (result: GalleryModalResult) => {
      backdrop.remove();
      resolve(result);
    };

    backdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === backdrop || target.closest('[data-action="cancel"]')) {
        close(null);
        return;
      }
      if (!target.closest('[data-action="save"]')) return;

      if (items.length < 2) {
        window.alert('Галерея должна содержать минимум 2 изображения.');
        return;
      }
      const badItem = items.findIndex((item) => !item.url.trim());
      if (badItem >= 0) {
        window.alert(`У изображения #${badItem + 1} не заполнен URL.`);
        return;
      }
      const layout = backdrop.querySelector<HTMLSelectElement>('#mj-gal-layout')?.value || 'grid';
      close({ layout, items: [...items] });
    });
  });

// ── Table modal with visual grid editor ─────────────────────────────────────

type TableModalResult = { columns: string[]; rows: string[][]; caption: string } | null;

const openTableModal = (initial?: Record<string, unknown>): Promise<TableModalResult> =>
  new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'mj-modal-backdrop';

    const initCols: string[] = Array.isArray(initial?.columns) ? (initial!.columns as string[]) : ['Столбец 1', 'Столбец 2'];
    const initRows: string[][] = Array.isArray(initial?.rows) ? (initial!.rows as string[][]) : [['', '']];
    const initCaption = getText(initial?.caption);

    backdrop.innerHTML = `
      <div class="mj-modal" role="dialog" aria-modal="true" style="width:min(760px,92vw)">
        <div class="mj-modal-head">Table</div>
        <div class="mj-modal-body">
          <div class="mj-table-editor" id="mj-tbl-wrap"></div>
          <div class="mj-table-controls">
            <button type="button" class="mj-btn" id="mj-tbl-add-col">+ Столбец</button>
            <button type="button" class="mj-btn" id="mj-tbl-add-row">+ Строка</button>
            <button type="button" class="mj-btn mj-btn-danger" id="mj-tbl-del-col">− Столбец</button>
            <button type="button" class="mj-btn mj-btn-danger" id="mj-tbl-del-row">− Строка</button>
          </div>
          <label class="mj-modal-label">Подпись
            <input class="mj-modal-input" id="mj-tbl-caption" value="${escapeAttribute(escapeHtml(initCaption))}" placeholder="Подпись под таблицей" />
          </label>
        </div>
        <div class="mj-modal-foot">
          <button type="button" data-action="cancel" class="mj-btn">Отмена</button>
          <button type="button" data-action="save" class="mj-btn mj-btn-primary">Сохранить</button>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    // mutable state
    const cols: string[] = [...initCols];
    const rows: string[][] = initRows.map((row) => {
      const r = [...row];
      while (r.length < cols.length) r.push('');
      return r.slice(0, cols.length);
    });

    const wrap = backdrop.querySelector<HTMLElement>('#mj-tbl-wrap')!;

    const readFromDom = () => {
      wrap.querySelectorAll<HTMLInputElement>('thead input[data-col]').forEach((inp) => {
        cols[Number(inp.dataset.col)] = inp.value;
      });
      wrap.querySelectorAll<HTMLInputElement>('tbody input[data-row][data-cell]').forEach((inp) => {
        const r = Number(inp.dataset.row);
        const c = Number(inp.dataset.cell);
        if (rows[r]) rows[r][c] = inp.value;
      });
    };

    const renderTable = () => {
      const thead = `<thead><tr>${cols.map((col, ci) => `<th><input data-col="${ci}" value="${escapeAttribute(escapeHtml(col))}" /></th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rows.map((row, ri) => `<tr>${row.map((cell, ci) => `<td><input data-row="${ri}" data-cell="${ci}" value="${escapeAttribute(escapeHtml(cell))}" /></td>`).join('')}</tr>`).join('')}</tbody>`;
      wrap.innerHTML = `<table>${thead}${tbody}</table>`;
    };

    renderTable();

    backdrop.querySelector('#mj-tbl-add-col')?.addEventListener('click', () => {
      readFromDom();
      cols.push(`Столбец ${cols.length + 1}`);
      rows.forEach((row) => row.push(''));
      renderTable();
    });

    backdrop.querySelector('#mj-tbl-del-col')?.addEventListener('click', () => {
      if (cols.length <= 1) return;
      readFromDom();
      cols.pop();
      rows.forEach((row) => row.pop());
      renderTable();
    });

    backdrop.querySelector('#mj-tbl-add-row')?.addEventListener('click', () => {
      readFromDom();
      rows.push(Array(cols.length).fill('') as string[]);
      renderTable();
    });

    backdrop.querySelector('#mj-tbl-del-row')?.addEventListener('click', () => {
      if (rows.length <= 1) return;
      readFromDom();
      rows.pop();
      renderTable();
    });

    const close = (result: TableModalResult) => {
      backdrop.remove();
      resolve(result);
    };

    backdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === backdrop || target.closest('[data-action="cancel"]')) {
        close(null);
        return;
      }
      if (!target.closest('[data-action="save"]')) return;

      readFromDom();
      const finalCols = cols.map((c) => c.trim()).filter(Boolean);
      if (!finalCols.length) { window.alert('Добавьте хотя бы один столбец.'); return; }
      const finalRows = rows.map((row) => row.map((cell) => cell.trim()));
      const caption = (backdrop.querySelector<HTMLInputElement>('#mj-tbl-caption')?.value || '').trim();
      close({ columns: finalCols, rows: finalRows, caption });
    });
  });

// ── Related links modal with row-based editor ────────────────────────────────

type LinkRow = { title: string; url: string; source: string };
type RelatedLinksModalResult = { title: string; items: LinkRow[] } | null;

const openRelatedLinksModal = (initial?: Record<string, unknown>): Promise<RelatedLinksModalResult> =>
  new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'mj-modal-backdrop';

    const initTitle = getText(initial?.title);
    const initItems: LinkRow[] = (Array.isArray(initial?.items) ? initial!.items : [{ title: '', url: '', source: '' }]).map((iv) => {
      const i = iv as Record<string, unknown>;
      return { title: getText(i.title), url: getText(i.url), source: getText(i.source) };
    });

    backdrop.innerHTML = `
      <div class="mj-modal" role="dialog" aria-modal="true" style="width:min(640px,92vw)">
        <div class="mj-modal-head">Related Links</div>
        <div class="mj-modal-body">
          <label class="mj-modal-label">Заголовок блока
            <input class="mj-modal-input" id="mj-links-title" value="${escapeAttribute(escapeHtml(initTitle))}" placeholder="Related links" />
          </label>
          <div id="mj-links-list" class="mj-links-list"></div>
          <button type="button" class="mj-btn" id="mj-links-add">+ Добавить ссылку</button>
        </div>
        <div class="mj-modal-foot">
          <button type="button" data-action="cancel" class="mj-btn">Отмена</button>
          <button type="button" data-action="save" class="mj-btn mj-btn-primary">Сохранить</button>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    const listEl = backdrop.querySelector<HTMLElement>('#mj-links-list')!;
    const linkRows: LinkRow[] = [...initItems];

    const renderRows = () => {
      listEl.innerHTML = linkRows
        .map(
          (row, idx) => `
          <div class="mj-link-row" data-link-idx="${idx}">
            <input class="mj-modal-input" data-link-field="title" data-link-idx="${idx}" value="${escapeAttribute(escapeHtml(row.title))}" placeholder="Заголовок *" />
            <input class="mj-modal-input" data-link-field="url" data-link-idx="${idx}" value="${escapeAttribute(escapeHtml(row.url))}" placeholder="https://... *" />
            <button type="button" class="mj-btn mj-btn-danger" data-link-remove="${idx}" title="Удалить">✕</button>
            <input class="mj-modal-input mj-link-row-source" data-link-field="source" data-link-idx="${idx}" value="${escapeAttribute(escapeHtml(row.source))}" placeholder="Источник (необязательно)" />
          </div>
        `
        )
        .join('');

      listEl.querySelectorAll<HTMLInputElement>('[data-link-field]').forEach((inp) => {
        inp.addEventListener('input', () => {
          const idx = Number(inp.dataset.linkIdx);
          const field = inp.dataset.linkField as 'title' | 'url' | 'source';
          if (linkRows[idx]) linkRows[idx][field] = inp.value;
        });
      });

      listEl.querySelectorAll<HTMLButtonElement>('[data-link-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = Number(btn.dataset.linkRemove);
          linkRows.splice(idx, 1);
          renderRows();
        });
      });
    };

    renderRows();

    backdrop.querySelector('#mj-links-add')?.addEventListener('click', () => {
      linkRows.push({ title: '', url: '', source: '' });
      renderRows();
      const inputs = listEl.querySelectorAll<HTMLInputElement>('[data-link-field="title"]');
      inputs[inputs.length - 1]?.focus();
    });

    const close = (result: RelatedLinksModalResult) => {
      backdrop.remove();
      resolve(result);
    };

    backdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === backdrop || target.closest('[data-action="cancel"]')) {
        close(null);
        return;
      }
      if (!target.closest('[data-action="save"]')) return;

      // validate
      for (const [i, row] of linkRows.entries()) {
        const t = row.title.trim();
        const u = row.url.trim();
        if (!t || !u) {
          window.alert(`Ссылка ${i + 1}: заголовок и URL обязательны.`);
          return;
        }
        if (!u.toLowerCase().startsWith('https://')) {
          window.alert(`Ссылка ${i + 1}: URL должен начинаться с https://`);
          return;
        }
      }
      if (!linkRows.length) { window.alert('Добавьте хотя бы одну ссылку.'); return; }

      const titleVal = (backdrop.querySelector<HTMLInputElement>('#mj-links-title')?.value || '').trim();
      close({ title: titleVal || 'Related links', items: linkRows.map((r) => ({ title: r.title.trim(), url: r.url.trim(), source: r.source.trim() })) });
    });
  });

const openTagsModal = (
  tagOptions: Array<{ id: number; name: string; slug: string }>,
  initial?: Record<string, unknown>
): Promise<TagsModalResult> =>
  new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'mj-modal-backdrop';

    const selectedIds = new Set(
      (Array.isArray(initial?.items) ? initial!.items : [])
        .map((itemValue) => Number((itemValue as Record<string, unknown>)?.id))
        .filter((value) => Number.isFinite(value))
    );

    backdrop.innerHTML = `
      <div class="mj-modal" role="dialog" aria-modal="true" style="width:min(640px,92vw)">
        <div class="mj-modal-head">Tags</div>
        <div class="mj-modal-body">
          <div class="mj-modal-help">Выберите теги из справочника системы.</div>
          <div id="mj-tags-list" style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;max-height:360px;overflow:auto;border:1px solid #e2e8f0;border-radius:10px;padding:10px;background:#f8fafc">
            ${tagOptions
              .map(
                (tag) => `
              <label style="display:flex;align-items:center;gap:8px;padding:6px;border:1px solid #e2e8f0;border-radius:8px;background:#fff">
                <input type="checkbox" data-tag-id="${tag.id}" ${selectedIds.has(tag.id) ? 'checked' : ''}/>
                <span style="font-size:12px;color:#0f172a">${escapeHtml(tag.name)} <span style="color:#64748b">#${escapeHtml(tag.slug)}</span></span>
              </label>
            `
              )
              .join('')}
          </div>
        </div>
        <div class="mj-modal-foot">
          <button type="button" data-action="cancel" class="mj-btn">Cancel</button>
          <button type="button" data-action="save" class="mj-btn mj-btn-primary">Save</button>
        </div>
      </div>
    `;

    const close = (result: TagsModalResult) => {
      backdrop.remove();
      resolve(result);
    };

    backdrop.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target === backdrop || target.closest('[data-action="cancel"]')) {
        close(null);
        return;
      }
      if (!target.closest('[data-action="save"]')) return;

      const checkedIds = Array.from(backdrop.querySelectorAll<HTMLInputElement>('input[data-tag-id]:checked'))
        .map((input) => Number(input.dataset.tagId))
        .filter((value) => Number.isFinite(value));

      if (!checkedIds.length) {
        window.alert('Выберите хотя бы один тег.');
        return;
      }

      const byId = new Map(tagOptions.map((tag) => [tag.id, tag]));
      const items = checkedIds
        .map((id) => byId.get(id))
        .filter((tag): tag is { id: number; name: string; slug: string } => Boolean(tag))
        .map((tag) => ({ id: tag.id, name: tag.name, slug: tag.slug }));

      close({ items });
    });

    document.body.appendChild(backdrop);
  });

// ── Generic field-based modal helpers ────────────────────────────────────────

const fieldsByType = (type: string): ModalField[] => {
  if (type === 'callout') {
    return [
      { key: 'variant', label: 'Variant', type: 'select', required: true, options: ['info', 'warning', 'success', 'danger'] },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'text', label: 'Text', type: 'textarea', required: true },
      { key: 'icon', label: 'Icon', type: 'text' },
    ];
  }
  if (type === 'embed') {
    return [
      { key: 'provider', label: 'Provider', type: 'select', required: true, options: ['youtube', 'vimeo', 'x', 'instagram', 'tiktok', 'other'] },
      { key: 'url', label: 'URL (https)', type: 'text', required: true },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'thumbnail', label: 'Thumbnail (https)', type: 'text' },
    ];
  }
  if (type === 'cta') {
    return [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'text', label: 'Text', type: 'textarea', required: true },
      { key: 'button_text', label: 'Button text', type: 'text', required: true },
      { key: 'button_url', label: 'Button URL (https)', type: 'text', required: true },
      { key: 'variant', label: 'Variant', type: 'select', options: ['primary', 'secondary'] },
    ];
  }
  if (type === 'code') {
    return [
      { key: 'content', label: 'Code', type: 'textarea', required: true },
      { key: 'language', label: 'Language', type: 'text' },
      { key: 'caption', label: 'Caption', type: 'text' },
    ];
  }
  if (type === 'divider') {
    return [{ key: 'style', label: 'Style', type: 'select', options: ['line', 'space'] }];
  }
  if (type === 'tags') {
    return [{ key: 'items', label: 'Items (id|name|slug per line)', type: 'textarea', required: true }];
  }
  if (type === 'list') {
    return [
      { key: 'style', label: 'Style', type: 'select', options: ['bullet', 'ordered', 'check'], required: true },
      { key: 'items', label: 'Items (one per line)', type: 'textarea', required: true },
    ];
  }
  return [];
};

const payloadFromValues = (type: string, values: Record<string, string>, id: string): ContentBlock => {
  if (type === 'callout') return { type, id, variant: values.variant || 'info', title: values.title || '', text: values.text || '', icon: values.icon || '' };
  if (type === 'embed') return { type, id, provider: values.provider || 'youtube', url: values.url || '', title: values.title || '', thumbnail: values.thumbnail || '' };
  if (type === 'cta') return { type, id, title: values.title || '', text: values.text || '', button_text: values.button_text || '', button_url: values.button_url || '', variant: values.variant || 'primary' };
  if (type === 'code') return { type, id, content: values.content || '', language: values.language || '', caption: values.caption || '' };
  if (type === 'divider') return { type, id, style: values.style || 'line' };
  if (type === 'tags') {
    const items = parseLines(values.items).map((line) => {
      const [idRaw, name, slug] = line.split('|').map((v) => v.trim());
      return { id: Number(idRaw) || 0, name: name || '', slug: slug || '' };
    });
    return { type, id, items };
  }
  if (type === 'list') return { type, id, style: values.style || 'bullet', items: parseLines(values.items) };
  return { type, id };
};

const extractLinksFromText = (text: string): ParagraphLink[] => {
  const links: ParagraphLink[] = [];
  const regex = /(https?:\/\/[^\s<]+)/gi;
  for (const match of text.matchAll(regex)) {
    const url = (match[0] || '').trim();
    if (url) links.push({ title: url, url });
  }
  return dedupeLinks(links);
};

const textNode = (text: string, href?: string): JSONContent =>
  href
    ? { type: 'text', text, marks: [{ type: 'link', attrs: { href } }] }
    : { type: 'text', text };

const textWithLinkNodes = (text: string, links: ParagraphLink[]): JSONContent[] => {
  if (!links.length) return text ? [textNode(text)] : [];

  let cursor = 0;
  const chunks: JSONContent[] = [];
  for (const link of links) {
    const idx = text.indexOf(link.title, cursor);
    if (idx < 0) continue;
    if (idx > cursor) chunks.push(textNode(text.slice(cursor, idx)));
    chunks.push(textNode(link.title, link.url));
    cursor = idx + link.title.length;
  }
  if (cursor < text.length) chunks.push(textNode(text.slice(cursor)));
  return chunks.length ? chunks : [textNode(text)];
};

const blockToTipTapNode = (block: ContentBlock): JSONContent => {
  if (block.type === 'paragraph') {
    const text = getText(block.text);

    // If inlines are present, restore rich inline formatting (bold, italic, underline, links)
    if (Array.isArray(block.inlines) && (block.inlines as unknown[]).length > 0) {
      const inlines = block.inlines as Array<{ text: string; marks?: string[]; href?: string }>;
      const content: JSONContent[] = inlines
        .filter((span) => span.text)
        .map((span) => {
          type TipTapMark = { type: string; attrs?: Record<string, unknown> };
          const tiptapMarks: TipTapMark[] = (span.marks || [])
            .map((mark): TipTapMark | null => {
              if (mark === 'link' && span.href) return { type: 'link', attrs: { href: span.href } };
              if (mark === 'bold') return { type: 'bold' };
              if (mark === 'italic') return { type: 'italic' };
              if (mark === 'underline') return { type: 'underline' };
              return null;
            })
            .filter((m): m is TipTapMark => m !== null);

          return tiptapMarks.length > 0
            ? ({ type: 'text', text: span.text, marks: tiptapMarks } as JSONContent)
            : textNode(span.text);
        });

      return {
        type: 'paragraph',
        attrs: { textAlign: getText(block.align) || 'left' },
        content: content.length ? content : [textNode('')],
      };
    }

    // Fallback: plain text with optional links (backward compatible with old data)
    const links = Array.isArray(block.links)
      ? dedupeLinks(
          (block.links as Array<Record<string, unknown>>)
            .map((item) => ({ title: getText(item.title), url: getText(item.url) }))
            .filter((item) => item.title && item.url)
        )
      : extractLinksFromText(text);

    return {
      type: 'paragraph',
      attrs: { textAlign: getText(block.align) || 'left' },
      content: textWithLinkNodes(text, links),
    };
  }

  if (block.type === 'heading') {
    return {
      type: 'heading',
      attrs: { level: [1, 2, 3, 4].includes(Number(block.level)) ? Number(block.level) : 2 },
      content: [textNode(getText(block.text))],
    };
  }

  if (block.type === 'list' && getText(block.style) !== 'check') {
    const listType = getText(block.style) === 'ordered' ? 'orderedList' : 'bulletList';
    const items = (Array.isArray(block.items) ? block.items : []).map((item) => ({
      type: 'listItem',
      content: [{ type: 'paragraph', content: [textNode(String(item))] }],
    }));
    return { type: listType, content: items };
  }

  if (block.type === 'quote') {
    return {
      type: 'blockquote',
      content: [{ type: 'paragraph', content: [textNode(getText(block.text))] }],
    };
  }

  if (block.type === 'code') {
    return {
      type: 'codeBlock',
      content: [textNode(getText(block.content))],
    };
  }

  if (block.type === 'divider' && getText(block.style) !== 'space') {
    return { type: 'horizontalRule' };
  }

  return { type: 'blockCard', attrs: { payload: block } };
};

const toText = (nodes: JSONContent[] | undefined): string =>
  (nodes || [])
    .map((node) => {
      if (node.type === 'text') return getText(node.text);
      return toText(node.content);
    })
    .join('');

const paragraphFromNode = (node: JSONContent): ContentBlock | null => {
  const parts = (node.content || []).filter((item) => item.type === 'text');
  const text = parts.map((part) => getText(part.text)).join('');
  if (!text.trim()) return null;

  const links: ParagraphLink[] = [];
  const inlines: InlineSpan[] = [];
  let hasRichMarks = false;

  for (const part of parts) {
    const partText = getText(part.text);
    const marks: string[] = [];
    let href: string | undefined;

    if (Array.isArray(part.marks)) {
      for (const mark of part.marks) {
        if (mark.type === 'link') {
          const url = mark.attrs?.href as string;
          if (url) {
            marks.push('link');
            href = url;
            links.push({ title: partText, url });
          }
        } else if (mark.type === 'bold' || mark.type === 'italic' || mark.type === 'underline') {
          marks.push(mark.type);
          hasRichMarks = true;
        }
      }
    }

    const span: InlineSpan = { text: partText, marks };
    if (href !== undefined) span.href = href;
    inlines.push(span);
  }

  const deduped = dedupeLinks(links);

  return {
    type: 'paragraph',
    id: uid(),
    text,
    align: getText(node.attrs?.textAlign) || 'left',
    ...(deduped.length ? { links: deduped } : {}),
    // Save inlines when any rich mark is present (enables round-trip of bold/italic/underline)
    ...(hasRichMarks ? { inlines } : {}),
  };
};

const blocksFromEditor = (editor: Editor): ContentBlock[] | null => {
  const json = editor.getJSON();
  const nodes = Array.isArray(json.content) ? json.content : [];
  const blocks: ContentBlock[] = [];

  for (const node of nodes) {
    if (node.type === 'blockCard') {
      const payload = parseBlockPayload(node.attrs?.payload);
      if (payload?.type) {
        blocks.push({ ...payload, id: String(payload.id || uid()) });
      }
      continue;
    }

    if (node.type === 'paragraph') {
      const parsed = paragraphFromNode(node);
      if (parsed) blocks.push(parsed);
      continue;
    }

    if (node.type === 'heading') {
      blocks.push({
        type: 'heading',
        id: uid(),
        level: Number(node.attrs?.level) || 2,
        text: toText(node.content),
      });
      continue;
    }

    if (node.type === 'bulletList' || node.type === 'orderedList') {
      const style = node.type === 'orderedList' ? 'ordered' : 'bullet';
      const items = (node.content || [])
        .map((item) => (typeof item === 'object' && item ? toText((item as JSONContent).content) : ''))
        .map((item) => item.trim())
        .filter(Boolean);
      if (items.length) blocks.push({ type: 'list', id: uid(), style, items });
      continue;
    }

    if (node.type === 'blockquote') {
      const text = toText(node.content).trim();
      if (text) blocks.push({ type: 'quote', id: uid(), text });
      continue;
    }

    if (node.type === 'codeBlock') {
      const content = toText(node.content);
      if (content.trim()) blocks.push({ type: 'code', id: uid(), content });
      continue;
    }

    if (node.type === 'horizontalRule') {
      blocks.push({ type: 'divider', id: uid(), style: 'line' });
    }
  }

  return blocks.length ? blocks : null;
};

const normalizeBlocks = (blocks: ContentBlock[]): ContentBlock[] =>
  blocks.map((block) => {
    if (block.type === 'paragraph') {
      const links = Array.isArray(block.links)
        ? (block.links as Array<Record<string, unknown>>).map((item) => ({
            title: getText(item.title),
            url: getText(item.url),
          }))
        : undefined;
      const inlines = Array.isArray(block.inlines)
        ? (block.inlines as Array<Record<string, unknown>>).map((span) => ({
            text: getText(span.text),
            marks: Array.isArray(span.marks) ? span.marks.map((mark) => getText(mark)).filter(Boolean) : [],
            ...(getText(span.href) ? { href: getText(span.href) } : {}),
          }))
        : undefined;
      return {
        ...block,
        text: getText(block.text),
        align: getText(block.align) || 'left',
        ...(links ? { links } : {}),
        ...(inlines ? { inlines } : {}),
      };
    }
    if (block.type === 'heading') {
      return { ...block, text: getText(block.text), level: Number(block.level) || 2 };
    }
    if (block.type === 'list') {
      return { ...block, style: getText(block.style) || 'bullet', items: (Array.isArray(block.items) ? block.items : []).map((item) => getText(item)) };
    }
    if (block.type === 'quote') {
      return { ...block, text: getText(block.text), author: getText(block.author), source: getText(block.source) };
    }
    if (block.type === 'callout') {
      return {
        ...block,
        variant: getText(block.variant) || 'info',
        title: getText(block.title),
        text: getText(block.text),
        icon: getText(block.icon),
      };
    }
    if (block.type === 'image') {
      return {
        ...block,
        url: getText(block.url),
        alt: getText(block.alt),
        caption: getText(block.caption),
        ratio: getText(block.ratio) || '16:9',
        credit: getText(block.credit),
      };
    }
    if (block.type === 'gallery') {
      return {
        ...block,
        layout: getText(block.layout) || 'grid',
        items: (Array.isArray(block.items) ? block.items : []).map((itemValue) => {
          const item = itemValue as Record<string, unknown>;
          return {
            url: getText(item.url),
            alt: getText(item.alt),
            caption: getText(item.caption),
            credit: getText(item.credit),
          };
        }),
      };
    }
    if (block.type === 'embed') {
      return {
        ...block,
        provider: getText(block.provider) || 'other',
        url: getText(block.url),
        title: getText(block.title),
        thumbnail: getText(block.thumbnail),
      };
    }
    if (block.type === 'table') {
      return {
        ...block,
        columns: (Array.isArray(block.columns) ? block.columns : []).map((column) => getText(column)),
        rows: (Array.isArray(block.rows) ? block.rows : []).map((rowValue) =>
          (Array.isArray(rowValue) ? rowValue : []).map((cell) => getText(cell))
        ),
        caption: getText(block.caption),
      };
    }
    if (block.type === 'related_links') {
      return {
        ...block,
        title: getText(block.title),
        items: (Array.isArray(block.items) ? block.items : []).map((itemValue) => {
          const item = itemValue as Record<string, unknown>;
          return { title: getText(item.title), url: getText(item.url), source: getText(item.source) };
        }),
      };
    }
    if (block.type === 'cta') {
      return {
        ...block,
        title: getText(block.title),
        text: getText(block.text),
        button_text: getText(block.button_text),
        button_url: getText(block.button_url),
        variant: getText(block.variant) || 'primary',
      };
    }
    if (block.type === 'code') {
      return { ...block, content: getText(block.content), language: getText(block.language), caption: getText(block.caption) };
    }
    if (block.type === 'divider') {
      return { ...block, style: getText(block.style) || 'line' };
    }
    if (block.type === 'tags') {
      return {
        ...block,
        items: (Array.isArray(block.items) ? block.items : []).map((itemValue) => {
          const item = itemValue as Record<string, unknown>;
          return { id: Number(item.id) || 0, name: getText(item.name), slug: getText(item.slug) };
        }),
      };
    }
    return block;
  });

const STRING_KEYS = new Set([
  'type',
  'id',
  'text',
  'align',
  'anchor',
  'style',
  'variant',
  'title',
  'icon',
  'url',
  'alt',
  'caption',
  'credit',
  'layout',
  'provider',
  'thumbnail',
  'source',
  'author',
  'button_text',
  'button_url',
  'language',
  'content',
  'name',
  'slug',
  'href',
]);

const deepSanitizeStringFields = (value: unknown, parentKey?: string): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => deepSanitizeStringFields(item, parentKey));
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    Object.entries(record).forEach(([key, current]) => {
      result[key] = deepSanitizeStringFields(current, key);
    });
    return result;
  }
  if (STRING_KEYS.has(String(parentKey || ''))) {
    return getText(value);
  }
  return value;
};

const validateBlocks = (blocks: ContentBlock[]): void => {
  blocks.forEach((block, index) => {
    if (!String(block.id || '').trim()) throw new Error(`Block ${index + 1}: id is required`);

    if (block.type === 'paragraph') {
      if (!getText(block.text).trim()) throw new Error(`Block ${index + 1}: paragraph text required`);
      return;
    }
    if (block.type === 'heading') {
      if (!getText(block.text).trim()) throw new Error(`Block ${index + 1}: heading text required`);
      if (![1, 2, 3, 4].includes(Number(block.level))) throw new Error(`Block ${index + 1}: heading level must be 1..4`);
      return;
    }
    if (block.type === 'list') {
      if (!['bullet', 'ordered', 'check'].includes(getText(block.style))) throw new Error(`Block ${index + 1}: list style invalid`);
      if (!Array.isArray(block.items) || block.items.length < 1) throw new Error(`Block ${index + 1}: list items required`);
      return;
    }
    if (block.type === 'quote') {
      if (!getText(block.text).trim()) throw new Error(`Block ${index + 1}: quote text required`);
      return;
    }
    if (block.type === 'callout') {
      if (!['info', 'warning', 'success', 'danger'].includes(getText(block.variant))) throw new Error(`Block ${index + 1}: callout variant invalid`);
      if (!getText(block.text).trim()) throw new Error(`Block ${index + 1}: callout text required`);
      return;
    }
    if (block.type === 'image') {
      if (!getText(block.url).trim()) throw new Error(`Block ${index + 1}: image url required`);
      return;
    }
    if (block.type === 'gallery') {
      if (!['carousel', 'grid'].includes(getText(block.layout))) throw new Error(`Block ${index + 1}: gallery layout invalid`);
      if (!Array.isArray(block.items) || block.items.length < 2) throw new Error(`Block ${index + 1}: gallery needs >= 2 items`);
      return;
    }
    if (block.type === 'embed') {
      if (!['youtube', 'vimeo', 'x', 'instagram', 'tiktok', 'other'].includes(getText(block.provider))) throw new Error(`Block ${index + 1}: embed provider invalid`);
      if (!getText(block.url).trim()) throw new Error(`Block ${index + 1}: embed url required`);
      return;
    }
    if (block.type === 'table') {
      if (!Array.isArray(block.columns) || block.columns.length < 1) throw new Error(`Block ${index + 1}: table columns required`);
      if (!Array.isArray(block.rows) || block.rows.length < 1) throw new Error(`Block ${index + 1}: table rows required`);
      return;
    }
    if (block.type === 'related_links') {
      if (!Array.isArray(block.items) || block.items.length < 1) throw new Error(`Block ${index + 1}: related links required`);
      for (const [itemIndex, item] of (block.items as Array<Record<string, unknown>>).entries()) {
        const title = getText(item.title);
        const url = getText(item.url);
        if (!title.trim()) throw new Error(`Block ${index + 1}: related_links item ${itemIndex + 1} title required`);
        if (!url.trim()) throw new Error(`Block ${index + 1}: related_links item ${itemIndex + 1} url required`);
        if (!url.toLowerCase().startsWith('https://')) {
          throw new Error(`Block ${index + 1}: related_links item ${itemIndex + 1} url must start with https://`);
        }
      }
      return;
    }
    if (block.type === 'cta') {
      if (!getText(block.title).trim() || !getText(block.text).trim() || !getText(block.button_text).trim() || !getText(block.button_url).trim()) {
        throw new Error(`Block ${index + 1}: cta fields required`);
      }
      return;
    }
    if (block.type === 'code') {
      if (!getText(block.content).trim()) throw new Error(`Block ${index + 1}: code content required`);
      return;
    }
    if (block.type === 'divider') {
      if (!['line', 'space'].includes(getText(block.style) || 'line')) throw new Error(`Block ${index + 1}: divider style invalid`);
      return;
    }
    if (block.type === 'tags') {
      if (!Array.isArray(block.items) || block.items.length < 1) throw new Error(`Block ${index + 1}: tags required`);
    }
  });
};

const toolbarMarkup = (): string => `
  <div class="mj-rich-toolbar-main">
    <button class="mj-icon-btn" type="button" data-cmd="undo" title="Undo"><span class="material-symbols-outlined">undo</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="redo" title="Redo"><span class="material-symbols-outlined">redo</span></button>
    <span class="mj-rich-sep"></span>
    <select class="mj-toolbar-select" data-select="heading" title="Heading">
      <option value="paragraph">Paragraph</option>
      <option value="h1">H1</option>
      <option value="h2">H2</option>
      <option value="h3">H3</option>
      <option value="h4">H4</option>
    </select>
    <button class="mj-icon-btn" type="button" data-cmd="bold" title="Bold"><span class="material-symbols-outlined">format_bold</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="italic" title="Italic"><span class="material-symbols-outlined">format_italic</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="underline" title="Underline"><span class="material-symbols-outlined">format_underlined</span></button>
    <span class="mj-rich-sep"></span>
    <button class="mj-icon-btn" type="button" data-cmd="bullet" title="Bullet list"><span class="material-symbols-outlined">format_list_bulleted</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="ordered" title="Ordered list"><span class="material-symbols-outlined">format_list_numbered</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="quote" title="Quote"><span class="material-symbols-outlined">format_quote</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="code" title="Code"><span class="material-symbols-outlined">code</span></button>
    <button class="mj-icon-btn" type="button" data-cmd="link" title="Link"><span class="material-symbols-outlined">link</span></button>
    <select class="mj-toolbar-select" data-select="align" title="Align">
      <option value="left">Align left</option>
      <option value="center">Align center</option>
      <option value="right">Align right</option>
    </select>
    <button type="button" class="mj-icon-btn" data-special="anchor" title="Anchor"><span class="material-symbols-outlined">anchor</span></button>
    <button type="button" class="mj-icon-btn" data-special="quote-meta" title="Quote Meta"><span class="material-symbols-outlined">format_quote</span></button>
    <button type="button" class="mj-icon-btn" data-insert="list" title="Check List"><span class="material-symbols-outlined">checklist</span></button>
    <button type="button" class="mj-icon-btn" data-insert="callout" title="Callout"><span class="material-symbols-outlined">info</span></button>
    <button type="button" class="mj-icon-btn" data-insert="image" title="Image"><span class="material-symbols-outlined">image</span></button>
    <button type="button" class="mj-icon-btn" data-insert="gallery" title="Gallery"><span class="material-symbols-outlined">collections</span></button>
    <button type="button" class="mj-icon-btn" data-insert="embed" title="Embed"><span class="material-symbols-outlined">smart_display</span></button>
    <button type="button" class="mj-icon-btn" data-insert="table" title="Table"><span class="material-symbols-outlined">table_chart</span></button>
    <button type="button" class="mj-icon-btn" data-insert="related_links" title="Links"><span class="material-symbols-outlined">link</span></button>
    <button type="button" class="mj-icon-btn" data-insert="cta" title="CTA"><span class="material-symbols-outlined">campaign</span></button>
    <button type="button" class="mj-icon-btn" data-insert="divider" title="Divider"><span class="material-symbols-outlined">horizontal_rule</span></button>
    <button type="button" class="mj-icon-btn" data-insert="tags" title="Tags"><span class="material-symbols-outlined">sell</span></button>
  </div>
`;

const BlockCard = Node.create({
  name: 'blockCard',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      payload: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const encoded = element.getAttribute('data-payload');
          if (!encoded) return null;
          const decoded = decodeURIComponent(encoded);
          return safeJsonParse<ContentBlock | null>(decoded, null);
        },
        renderHTML: (attributes: { payload?: ContentBlock | null }) => {
          const payload = attributes.payload || null;
          return {
            'data-payload': encodeURIComponent(JSON.stringify(payload || {})),
          };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-block-card="1"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-block-card': '1' }, HTMLAttributes)];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'mj-q-card';
      const payload =
        parseBlockPayload(node.attrs.payload) || ({ type: 'callout', id: uid(), variant: 'info', text: '' } as ContentBlock);
      dom.dataset.payload = JSON.stringify(payload);
      dom.dataset.blockId = String(payload.id || '');
      dom.innerHTML = cardInnerHtml(payload);
      dom.setAttribute('contenteditable', 'false');
      return { dom };
    };
  },
});

const findBlockCardPosById = (editor: Editor, id: string): number | null => {
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    const payload = parseBlockPayload(node.attrs?.payload);
    if (node.type.name === 'blockCard' && String(payload?.id || '') === id) {
      found = pos;
      return false;
    }
    return true;
  });
  return found;
};

export const createRichContentEditor = ({ root, onChange, uploadImage, getTagOptions }: RichEditorOptions): RichEditorHandle => {
  createInlineStyles();

  root.innerHTML = `
    <div class="mj-rich-shell">
      <div class="mj-rich-toolbar">${toolbarMarkup()}</div>
      <div class="mj-editor-body"><div id="mj-rich-editor-target" class="mj-editor-surface"></div></div>
    </div>
  `;

  let disposed = false;
  let queuedBlocks: ContentBlock[] | null = null;
  let pendingInsertPos: number | null = null;

  const target = root.querySelector<HTMLElement>('#mj-rich-editor-target');
  if (!target) throw new Error('Editor target not found.');

  const editor = new Editor({
    element: target,
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
      }),
      Heading.configure({ levels: [1, 2, 3, 4] }),
      Blockquote,
      Underline,
      Link.configure({ openOnClick: true, autolink: true, linkOnPaste: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Введите текст и форматируйте как в документе.' }),
      BlockCard,
    ],
    content: '<p></p>',
    editorProps: {
      attributes: {
        class: 'mj-editor-content',
      },
    },
    onUpdate: () => {
      if (disposed) return;
      try {
        onChange?.(blocksFromEditor(editor) || []);
      } catch {
        // ignore live parse errors
      }
    },
  });

  const applyBlocks = (blocks: ContentBlock[]) => {
    const content = blocks.map(blockToTipTapNode);
    editor.commands.setContent({ type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] });
    onChange?.(blocks);
  };

  const insertCard = (payload: ContentBlock, position?: number | null) => {
    const node = blockToTipTapNode(payload);
    const from = Math.max(0, Number(position ?? editor.state.selection.from ?? 0));
    const inserted = editor.commands.insertContentAt(from, node);
    if (inserted) {
      editor.commands.focus();
      return;
    }

    // Fallback for invalid selection state: append to the end.
    const currentBlocks = blocksFromEditor(editor) || [];
    currentBlocks.push(payload);
    applyBlocks(currentBlocks);
    editor.commands.focus('end');
  };

  const openSpecialModal = async (type: string, editingPayload?: ContentBlock) => {
    let payload: ContentBlock | null = null;

    // ── image: use rich file-upload modal ──────────────────────────────────
    if (type === 'image') {
      const result = await openImageModal(uploadImage, editingPayload ? { ...editingPayload } : undefined);
      if (!result) return;
      payload = { type, id: String(editingPayload?.id || uid()), ...result };
    }

    // ── gallery: use rich multi-file modal ─────────────────────────────────
    else if (type === 'gallery') {
      const result = await openGalleryModal(uploadImage, editingPayload ? { ...editingPayload } : undefined);
      if (!result) return;
      payload = { type, id: String(editingPayload?.id || uid()), layout: result.layout, items: result.items };
    }

    // ── table: use visual grid editor ──────────────────────────────────────
    else if (type === 'table') {
      const result = await openTableModal(editingPayload ? { ...editingPayload } : undefined);
      if (!result) return;
      payload = { type, id: String(editingPayload?.id || uid()), ...result };
    }

    // ── related_links: use row-based editor ────────────────────────────────
    else if (type === 'related_links') {
      const result = await openRelatedLinksModal(editingPayload ? { ...editingPayload } : undefined);
      if (!result) return;
      payload = { type, id: String(editingPayload?.id || uid()), title: result.title, items: result.items };
    }

    // ── tags: pick existing tags from system dictionary ────────────────────
    else if (type === 'tags') {
      const options = (getTagOptions?.() || []).filter((item) => Number.isFinite(item.id));
      if (!options.length) {
        window.alert('Список тегов пуст. Загрузите теги в справочнике и повторите.');
        return;
      }
      const result = await openTagsModal(options, editingPayload ? { ...editingPayload } : undefined);
      if (!result) return;
      payload = { type, id: String(editingPayload?.id || uid()), items: result.items };
    }

    // ── all other block types: generic field-based form ────────────────────
    else {
      const fields = fieldsByType(type);
      if (!fields.length) return;

      let initial: Record<string, unknown> = editingPayload ? { ...editingPayload } : {};
      if (type === 'tags') {
        initial.items = (Array.isArray(initial.items) ? initial.items : [])
          .map((itemValue) => {
            const item = itemValue as Record<string, unknown>;
            return [item.id, item.name, item.slug].map((v) => String(v || '').trim()).join('|');
          })
          .join('\n');
      }
      if (type === 'list') {
        initial.items = Array.isArray(initial.items) ? (initial.items as string[]).join('\n') : '';
      }

      const values = await modalForm(`Configure ${type}`, fields, initial);
      if (!values) return;
      payload = payloadFromValues(type, values, String(initial.id || uid()));
    }

    if (!payload) return;

    if (editingPayload) {
      const pos = findBlockCardPosById(editor, String(editingPayload.id || ''));
      if (pos !== null) {
        const tr = editor.state.tr.setNodeMarkup(pos, undefined, { payload });
        editor.view.dispatch(tr);
      }
      return;
    }

    insertCard(payload, pendingInsertPos);
    pendingInsertPos = null;
  };

  // Run a sync toolbar command immediately (no async, no modals).
  const runSyncCmd = (cmd: string) => {
    if (cmd === 'undo') editor.chain().focus().undo().run();
    else if (cmd === 'redo') editor.chain().focus().redo().run();
    else if (cmd === 'bold') editor.chain().focus().toggleBold().run();
    else if (cmd === 'italic') editor.chain().focus().toggleItalic().run();
    else if (cmd === 'underline') editor.chain().focus().toggleUnderline().run();
    else if (cmd === 'bullet') editor.chain().focus().toggleBulletList().run();
    else if (cmd === 'ordered') editor.chain().focus().toggleOrderedList().run();
    else if (cmd === 'quote') editor.chain().focus().toggleBlockquote().run();
    else if (cmd === 'code') editor.chain().focus().toggleCodeBlock().run();
    else if (cmd === 'align-left') editor.chain().focus().setTextAlign('left').run();
    else if (cmd === 'align-center') editor.chain().focus().setTextAlign('center').run();
    else if (cmd === 'align-right') editor.chain().focus().setTextAlign('right').run();
  };

  // Handle async toolbar commands (those that open a modal dialog).
  const runAsyncCmd = (cmd: string) => {
    if (cmd !== 'link') return;
    void (async () => {
      if (editor.state.selection.empty) {
        const values = await modalForm(
          'Insert link',
          [
            { key: 'text', label: 'Text', type: 'text', required: true },
            { key: 'url', label: 'URL', type: 'text', required: true },
          ],
          { text: '', url: 'https://' }
        );
        if (!values) return;
        editor
          .chain()
          .focus()
          .insertContent([{ type: 'text', text: values.text, marks: [{ type: 'link', attrs: { href: values.url } }] }])
          .run();
      } else {
        const values = await modalForm('Set link', [{ key: 'url', label: 'URL', type: 'text', required: true }], { url: 'https://' });
        if (!values) return;
        editor.chain().focus().setLink({ href: values.url }).run();
      }
    })();
  };

  // mousedown handles all SYNC toolbar commands.
  // Running commands here (not on click) is the standard TipTap pattern:
  //   - preventDefault() keeps focus + selection inside the editor
  //   - command runs before the browser can clear the selection
  const onRootMouseDown = (event: Event) => {
    const targetElement = event.target as HTMLElement;

    const cmdBtn = targetElement.closest<HTMLButtonElement>('button[data-cmd]');
    if (cmdBtn) {
      const cmd = cmdBtn.dataset.cmd || '';
      if (cmd === 'link') {
        // link opens an async modal — handle on click instead, just prevent default here
        event.preventDefault();
      } else {
        event.preventDefault();
        runSyncCmd(cmd);
      }
      return;
    }

    // For insert/special buttons, just prevent default to keep editor focus;
    // the actual action (modal) is handled on click.
    if (targetElement.closest<HTMLElement>('button.mj-icon-btn')) {
      event.preventDefault();
    }
  };

  // click handles only ASYNC toolbar commands (modals) and block-card interactions.
  const onRootClick = (event: Event) => {
    const targetElement = event.target as HTMLElement;

    const cmdBtn = targetElement.closest<HTMLButtonElement>('button[data-cmd]');
    if (cmdBtn) {
      runAsyncCmd(cmdBtn.dataset.cmd || '');
      return;
    }

    const insertBtn = targetElement.closest<HTMLButtonElement>('button[data-insert]');
    if (insertBtn) {
      pendingInsertPos = editor.state.selection.from;
      const type = insertBtn.dataset.insert || '';
      if (type) void openSpecialModal(type);
      return;
    }

    const specialBtn = targetElement.closest<HTMLButtonElement>('button[data-special]');
    if (specialBtn) {
      const action = specialBtn.dataset.special || '';
      if (action === 'anchor') {
        window.alert('Anchor для heading в Tiptap добавлю следующим шагом.');
      } else if (action === 'quote-meta') {
        window.alert('Quote meta (author/source) в Tiptap добавлю следующим шагом.');
      }
      return;
    }

    const removeBtn = targetElement.closest<HTMLButtonElement>('[data-card-remove]');
    if (removeBtn) {
      const id = String(removeBtn.dataset.cardRemove || '').trim();
      if (!id) return;
      const pos = findBlockCardPosById(editor, id);
      if (pos !== null) {
        const node = editor.state.doc.nodeAt(pos);
        if (node) editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      }
      return;
    }

    const editBtn = targetElement.closest<HTMLButtonElement>('[data-card-edit]');
    if (editBtn) {
      const id = String(editBtn.dataset.cardEdit || '').trim();
      if (!id) return;
      const pos = findBlockCardPosById(editor, id);
      if (pos === null) return;
      const node = editor.state.doc.nodeAt(pos);
      const payload = parseBlockPayload(node?.attrs?.payload);
      if (!payload?.type) return;
      void openSpecialModal(String(payload.type), payload);
      return;
    }

    const clickedCard = targetElement.closest<HTMLElement>('.mj-q-card');
    if (clickedCard) {
      const payload = safeJsonParse<ContentBlock | null>(clickedCard.dataset.payload || '{}', null);
      if (!payload?.type) return;
      void openSpecialModal(String(payload.type), payload);
    }
  };

  const onRootChange = (event: Event) => {
    const target = event.target as HTMLElement;
    const select = target.closest<HTMLSelectElement>('select[data-select]');
    if (!select) return;

    if (select.dataset.select === 'heading') {
      const value = select.value;
      if (value === 'paragraph') {
        editor.chain().focus().setParagraph().run();
      } else if (value === 'h1' || value === 'h2' || value === 'h3' || value === 'h4') {
        editor.chain().focus().toggleHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 | 4 }).run();
      }
      return;
    }

    if (select.dataset.select === 'align') {
      const value = select.value;
      if (value === 'left' || value === 'center' || value === 'right') {
        editor.chain().focus().setTextAlign(value).run();
      }
    }
  };

  root.addEventListener('mousedown', onRootMouseDown);
  root.addEventListener('click', onRootClick);
  root.addEventListener('change', onRootChange);

  if (queuedBlocks) {
    applyBlocks(queuedBlocks);
    queuedBlocks = null;
  } else {
    onChange?.([]);
  }

  return {
    getBlocks: () => {
      const blocks = blocksFromEditor(editor);
      if (!blocks || !blocks.length) return null;
      const normalized = normalizeBlocks(blocks).map((block) => deepSanitizeStringFields(block) as ContentBlock);
      validateBlocks(normalized);
      return normalized;
    },
    setBlocks: (blocks?: ContentBlock[] | null) => {
      const list = Array.isArray(blocks) ? blocks : [];
      applyBlocks(list);
    },
    destroy: () => {
      disposed = true;
      root.removeEventListener('mousedown', onRootMouseDown);
      root.removeEventListener('click', onRootClick);
      root.removeEventListener('change', onRootChange);
      editor.destroy();
    },
  };
};
